// apps/client/src/engine/silhouette.ts (light and look, 2026-10-16) — the other half of the see-through. Trees and houses that hide a
// resident fade whole (engine/shader/fade.ts); what never fades (a terrace's edge you stand right behind, a fence, a rock) leaves them
// as a soft silhouette instead: a twin of the doll's skinned mesh, drawn after everything opaque only where something nearer hides the
// doll (depth test GREATER), and never over the doll's own visible pixels, which the doll marks in the stencil buffer as it draws.
// Every doll marks the stencil (fixer, 2026-10-18: `markDoll` on each baked doll material, characters/doll.ts), so a villager, a
// shopkeeper or another resident standing in front of you hides your silhouette there instead of being washed over by it.
import { tuning } from '@whiskerton/content';
import * as THREE from 'three';

/** Drawn after every opaque thing (the doll included, so its stencil marks are there) and before the faded objects' ghosts. */
export const SILHOUETTE_ORDER = 899;
/** The doll itself draws after the opaque world (render order 0), so the stencil it marks is only what still shows of it: drawn first,
 *  it would mark its whole body and a terrace edge drawn over its legs afterwards would leave them marked, with no silhouette. */
export const DOLL_ORDER = 1;
/** The stencil value every doll writes where it shows, and the silhouette skips. */
export const DOLL_STENCIL = 1;
const STENCIL = DOLL_STENCIL;
const S = tuning.light.fade.silhouette;
let mat: THREE.MeshBasicMaterial | null = null;
/** The one silhouette material: a soft flat tint (tuning.light.fade.silhouette) wherever a doll is hidden and not itself showing. */
export function silhouetteMaterial(): THREE.MeshBasicMaterial {
  mat ??= new THREE.MeshBasicMaterial({ color: S.color, transparent: true, opacity: S.opacity, depthFunc: THREE.GreaterDepth, depthWrite: false,
    fog: false, stencilWrite: true, stencilRef: STENCIL, stencilFunc: THREE.NotEqualStencilFunc,
    stencilFail: THREE.KeepStencilOp, stencilZFail: THREE.KeepStencilOp, stencilZPass: THREE.KeepStencilOp });
  return mat;
}
/** Makes `material` mark the stencil wherever a doll drawn with it shows (it passes the depth test), so no silhouette covers it there.
 *  Every baked doll material gets this when it is made (characters/doll.ts), villagers' and NPCs' as well as residents'. */
export function markDoll(material: THREE.Material): void {
  material.stencilWrite = true; material.stencilRef = STENCIL; material.stencilFunc = THREE.AlwaysStencilFunc;
  material.stencilFail = THREE.KeepStencilOp; material.stencilZFail = THREE.KeepStencilOp; material.stencilZPass = THREE.ReplaceStencilOp;
}
/** Gives a resident's doll its silhouette: each visible skinned mesh marks the stencil where it shows and gets a twin sharing its
 *  geometry and skeleton. Idempotent; the twins go with the doll (Avatar.dispose frees the shared geometry once). */
export function addSilhouette(root: THREE.Object3D): void {
  const skinned: THREE.SkinnedMesh[] = [];
  let done = false;
  root.traverse((o) => {
    if (o.userData.silhouette) done = true;
    const m = o as THREE.SkinnedMesh;
    if (m.isSkinnedMesh && m.visible && !o.userData.silhouette) skinned.push(m);
  });
  if (done) return;
  for (const m of skinned) {
    m.renderOrder = Math.max(m.renderOrder, DOLL_ORDER);
    // Baked doll materials mark the stencil from the start (markDoll); this covers a doll drawn with any other material.
    for (const own of Array.isArray(m.material) ? m.material : [m.material]) markDoll(own);
    const twin = new THREE.SkinnedMesh(m.geometry, silhouetteMaterial());
    twin.name = 'silhouette';
    twin.userData.silhouette = true;                      // out of GTAO's prepass (engine/renderer.ts)
    twin.position.copy(m.position); twin.quaternion.copy(m.quaternion); twin.scale.copy(m.scale);
    twin.bind(m.skeleton, m.bindMatrix);
    twin.bindMode = m.bindMode;
    twin.renderOrder = SILHOUETTE_ORDER;
    twin.castShadow = twin.receiveShadow = false;
    twin.frustumCulled = m.frustumCulled;
    m.parent!.add(twin);
  }
}
